import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Stack,
  FormControl,
  Select,
  Option,
  Input,
  Button,
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
} from '@mui/joy';
import { CalendarToday, CheckCircle, Warning } from '@mui/icons-material';
import { scheduleService } from '@/services/scheduleService';
import { shiftTypeService } from '@/services/shiftTypeService';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';
import toast from 'react-hot-toast';

// Global cache for bulk modal data to prevent excessive API calls
let cachedBulkShiftTypes: ShiftType[] | null = null;
let lastBulkFetchTime = 0;
const BULK_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface BulkScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface BulkScheduleFormData {
  employee_id: number | null;
  shift_type_id: number | null;
  start_time: string;
  end_time: string;
  selected_dates: string[];
  status: 'draft' | 'published';
}

interface BulkScheduleFormErrors {
  employee_id?: string;
  shift_type_id?: string;
  start_time?: string;
  end_time?: string;
  selected_dates?: string;
  general?: string;
}

export default function BulkScheduleModal({ open, onClose, onSuccess }: BulkScheduleModalProps) {
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [qualifiedEmployees, setQualifiedEmployees] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const mountedRef = useRef(true);

  const [formData, setFormData] = useState<BulkScheduleFormData>({
    employee_id: null,
    shift_type_id: null,
    start_time: '',
    end_time: '',
    selected_dates: [],
    status: 'draft',
  });

  const [errors, setErrors] = useState<BulkScheduleFormErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch shift types and employees on mount with caching
  useEffect(() => {
    if (open) {
      fetchInitialData();
    }
  }, [open]);

  const fetchInitialData = async () => {
    const now = Date.now();
    
    // Check if we have valid cached data
    if (cachedBulkShiftTypes && (now - lastBulkFetchTime) < BULK_CACHE_DURATION) {
      setShiftTypes(cachedBulkShiftTypes);
      return;
    }

    setIsLoading(true);
    try {
      const [shiftTypesData] = await Promise.all([
        shiftTypeService.getAllShiftTypes(),
      ]);
      
      // Only update state if component is still mounted
      if (mountedRef.current) {
        setShiftTypes(shiftTypesData);
        
        // Update cache
        cachedBulkShiftTypes = shiftTypesData;
        lastBulkFetchTime = now;
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      
      // Use cached data on error if available
      if (cachedBulkShiftTypes && mountedRef.current) {
        setShiftTypes(cachedBulkShiftTypes);
      } else {
        toast.error('Failed to load shift types');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const fetchQualifiedEmployees = async (shiftTypeId: number | null) => {
    if (!shiftTypeId) {
      setQualifiedEmployees([]);
      return;
    }

    try {
      const qualifiedEmployeesData = await shiftTypeService.getEmployeesForShiftType(shiftTypeId);
      setQualifiedEmployees(qualifiedEmployeesData);
    } catch (error) {
      console.error('Failed to fetch qualified employees:', error);
      toast.error('Failed to load qualified employees');
      setQualifiedEmployees([]);
    }
  };

  const handleShiftTypeChange = async (shiftTypeId: number | null) => {
    setFormData(prev => ({
      ...prev,
      shift_type_id: shiftTypeId,
      employee_id: null, // Reset employee when shift type changes
    }));
    setErrors(prev => ({ ...prev, shift_type_id: undefined, employee_id: undefined }));

    if (shiftTypeId) {
      await fetchQualifiedEmployees(shiftTypeId);
      
      // Set default times if available
      const selectedShiftType = shiftTypes.find(st => st.id === shiftTypeId);
      if (selectedShiftType && selectedShiftType.default_start_time && selectedShiftType.default_end_time) {
        // Trim to HH:mm — the API may return HH:mm:ss from the database
        const toHHmm = (t: string) => t.slice(0, 5);
        setFormData(prev => ({
          ...prev,
          start_time: toHHmm(selectedShiftType.default_start_time!),
          end_time: toHHmm(selectedShiftType.default_end_time!),
        }));
      }
    } else {
      setQualifiedEmployees([]);
    }
  };

  const handleDateSelection = (date: string) => {
    setFormData(prev => {
      const newDates = prev.selected_dates.includes(date)
        ? prev.selected_dates.filter(d => d !== date)
        : [...prev.selected_dates, date];
      
      return {
        ...prev,
        selected_dates: newDates
      };
    });
    setErrors(prev => ({ ...prev, selected_dates: undefined }));
  };

  const handleTimeChange = (field: 'start_time' | 'end_time', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTouchedFields(prev => new Set([...prev, field]));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (showAllErrors: boolean = false): boolean => {
    const newErrors: BulkScheduleFormErrors = {};

    if (!formData.shift_type_id) {
      newErrors.shift_type_id = 'Shift type is required';
    }

    if (!formData.employee_id) {
      newErrors.employee_id = 'Employee is required';
    }

    // Only validate time fields if they've been touched or if we're showing all errors (on submit)
    if (showAllErrors || touchedFields.has('start_time')) {
      if (!formData.start_time) {
        newErrors.start_time = 'Start time is required';
      } else {
        // Validate time format (HH:mm) - HTML time input returns format like "09:00"
        const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(formData.start_time)) {
          newErrors.start_time = 'Invalid time format. Use HH:mm format (e.g., 09:00)';
        }
      }
    }

    if (showAllErrors || touchedFields.has('end_time')) {
      if (!formData.end_time) {
        newErrors.end_time = 'End time is required';
      } else {
        // Validate time format (HH:mm) - HTML time input returns format like "09:00"
        const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(formData.end_time)) {
          newErrors.end_time = 'Invalid time format. Use HH:mm format (e.g., 09:00)';
        }
      }
    }

    // Always validate time comparison if both times are present
    if (formData.start_time && formData.end_time) {
      const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
      if (timeRegex.test(formData.start_time) && timeRegex.test(formData.end_time)) {
        const startTime = new Date(`2000-01-01T${formData.start_time}`);
        const endTime = new Date(`2000-01-01T${formData.end_time}`);
        
        if (startTime >= endTime) {
          newErrors.end_time = 'End time must be after start time';
        }
      }
    }

    if (formData.selected_dates.length === 0) {
      newErrors.selected_dates = 'At least one date must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(true)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await scheduleService.bulkCreateSchedules({
        employee_id: formData.employee_id!,
        shift_type_id: formData.shift_type_id!,
        start_time: formData.start_time,
        end_time: formData.end_time,
        selected_dates: formData.selected_dates,
        status: formData.status,
      });

      setResult(result);
      
      if (result.summary.created > 0) {
        toast.success(`Successfully created ${result.summary.created} schedules`);
        onSuccess();
        // Close the modal after successful creation
        handleClose();
      } else if (result.summary.conflicts > 0 || result.summary.errors > 0) {
        // Keep modal open if there were only conflicts or errors
        if (result.summary.conflicts > 0) {
          toast.error(`${result.summary.conflicts} schedules had conflicts`);
        }
        if (result.summary.errors > 0) {
          toast.error(`${result.summary.errors} schedules failed to create`);
        }
      }
    } catch (error: any) {
      console.error('Bulk schedule creation failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create schedules';
      toast.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      employee_id: null,
      shift_type_id: null,
      start_time: '',
      end_time: '',
      selected_dates: [],
      status: 'draft',
    });
    setErrors({});
    setTouchedFields(new Set());
    setResult(null);
    onClose();
  };

  // Generate dates for the next 4 weeks (28 days)
  const generateDateOptions = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 28; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const dateOptions = generateDateOptions();

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog 
        size="lg" 
        maxWidth="md"
        sx={{
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}
      >
        <ModalClose />
        <Typography level="h4" mb={2}>
          Bulk Schedule Creation
        </Typography>

        <form onSubmit={handleSubmit} style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flex: 1,
          minHeight: 0
        }}>
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}>
            <Stack spacing={3}>
            {errors.general && (
              <Alert color="danger" variant="soft">
                {errors.general}
              </Alert>
            )}

            <FormControl error={!!errors.shift_type_id}>
              <Typography level="body-sm" mb={1}>
                Shift Type *
              </Typography>
              <Select
                placeholder="Select shift type"
                value={formData.shift_type_id ? String(formData.shift_type_id) : ''}
                onChange={(_, value) => handleShiftTypeChange(value ? Number(value) : null)}
                disabled={isLoading}
              >
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={String(shiftType.id)}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                        }}
                      />
                      {shiftType.name}
                    </Stack>
                  </Option>
                ))}
              </Select>
              {errors.shift_type_id && (
                <Typography level="body-xs" color="danger" mt={1}>
                  {errors.shift_type_id}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.employee_id}>
              <Typography level="body-sm" mb={1}>
                Employee *
              </Typography>
              <Select
                placeholder={formData.shift_type_id ? "Select employee" : "Select shift type first"}
                value={formData.employee_id ? String(formData.employee_id) : ''}
                onChange={(_, value) => setFormData({ ...formData, employee_id: value ? Number(value) : null })}
                disabled={!formData.shift_type_id || isLoading}
              >
                {qualifiedEmployees.map((employee) => (
                  <Option key={employee.id} value={String(employee.id)}>
                    {employee.first_name} {employee.last_name}
                  </Option>
                ))}
              </Select>
              {errors.employee_id && (
                <Typography level="body-xs" color="danger" mt={1}>
                  {errors.employee_id}
                </Typography>
              )}
              {formData.shift_type_id && qualifiedEmployees.length === 0 && (
                <Typography level="body-xs" color="warning" mt={1}>
                  No employees are qualified for this shift type
                </Typography>
              )}
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormControl error={!!errors.start_time}>
                  <Typography level="body-sm" mb={1}>
                    Start Time *
                  </Typography>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => handleTimeChange('start_time', e.target.value)}
                    error={!!errors.start_time}
                    size="lg"
                  />
                  {errors.start_time && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.start_time}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl error={!!errors.end_time}>
                  <Typography level="body-sm" mb={1}>
                    End Time *
                  </Typography>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => handleTimeChange('end_time', e.target.value)}
                    error={!!errors.end_time}
                    size="lg"
                  />
                  {errors.end_time && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.end_time}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <FormControl>
              <Typography level="body-sm" mb={1}>
                Status
              </Typography>
              <Select
                value={formData.status}
                onChange={(_, value) => setFormData({ ...formData, status: value || 'draft' })}
              >
                <Option value="draft">Draft</Option>
                <Option value="published">Published</Option>
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Typography level="body-sm" mb={2}>
                Select Dates * ({formData.selected_dates.length} selected)
              </Typography>
              {errors.selected_dates && (
                <Typography level="body-xs" color="danger" mb={1}>
                  {errors.selected_dates}
                </Typography>
              )}
              
              <Box sx={{ 
                maxHeight: '150px', 
                overflowY: 'auto', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 'sm',
                p: 1
              }}>
                <Grid container spacing={1}>
                  {dateOptions.map((date) => {
                    const isSelected = formData.selected_dates.includes(date);
                    const dateObj = new Date(date);
                    const isToday = dateObj.toDateString() === new Date().toDateString();
                    
                    return (
                      <Grid xs={6} sm={4} md={3} key={date}>
                        <Button
                          variant={isSelected ? 'solid' : 'outlined'}
                          size="sm"
                          onClick={() => handleDateSelection(date)}
                          sx={{ 
                            width: '100%',
                            justifyContent: 'flex-start',
                            fontSize: 'xs',
                            height: '32px'
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                          {dateObj.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            weekday: 'short'
                          })}
                          {isToday && (
                            <Chip size="sm" color="primary" sx={{ ml: 0.5 }}>
                              Today
                            </Chip>
                          )}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

            {result && (
              <Alert 
                color={result.summary.errors > 0 ? 'warning' : 'success'} 
                variant="soft"
                startDecorator={result.summary.errors > 0 ? <Warning /> : <CheckCircle />}
              >
                <Typography level="body-sm">
                  Created: {result.summary.created} | 
                  Conflicts: {result.summary.conflicts} | 
                  Errors: {result.summary.errors}
                </Typography>
              </Alert>
            )}
          </Stack>
          </Box>

          {/* Fixed button area outside scrollable content */}
          <Box sx={{ 
            borderTop: '1px solid', 
            borderColor: 'divider', 
            p: 2, 
            backgroundColor: 'background.surface',
            flexShrink: 0,
            minHeight: 'fit-content'
          }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                loading={isSubmitting}
                disabled={formData.selected_dates.length === 0}
              >
                Create {formData.selected_dates.length} Schedule{formData.selected_dates.length !== 1 ? 's' : ''}
              </Button>
            </Stack>
          </Box>
        </form>
      </ModalDialog>
    </Modal>
  );
} 